import { substitute } from "../../utils/String"
import { triggerEvent } from "../../utils/dom"
import Emitter from "./Emitter"
import { getElementRef } from "./utils/getElementRef"

export const STATES = {
    OPEN: "is-open",
    DISABLED: "is-disabled",
    IS_PROMPT_SELECTED: "is-prompt-selected",
    IS_ITEM_SELECTED: "is-item-selected",
}

export const EMITS = {
    CHANGE: "change",
    CLOSE: "close",
    OPEN: "open",
    UPDATE_ALL_CHOICES: "choices-update",
    RENDER: "render",
    RENDER_CHOICES_BEFORE: "render:choices:before",
    RENDER_CHOICES: "render:choices",
}

export const TRIGGER_TYPES = {
    SELECT: "select",
    NONE: "none",
}

export default class SelectCore extends Emitter {
    constructor(element, options = {}) {
        super()

        this.element = element
        this.options = {
            choiceTemplate: '<span class="Select-choice Select-choice--listitem">{label0}</span>',
            placeholderChoiceTemplate: '<span class="Select-choice Select-choice--placeholder">{label0}</span>',
            selectTemplate:
                '<select class="Select-select"><option value="">empty</option><option value="1">:]</option></select>',
            placeholderTemplate: '<div class="Select-placeholder" data-placeholder></div>',
            dropdownTemplate: '<div class="Select-dropdown" data-dropdown></div>',
            listTemplate: '<div class="Select-list" data-list></div>',
            ...options,
        }
        this.ref = {}
        this.choices = []
        this.plugins = []

        this.closeTimer = null
        this.isListMouseDown = false
    }

    setup() {
        this.options = {
            ...this.options,
            ...this.plugins.reduce((acc, plugin) => ({ ...acc, ...plugin.options() }), {}),
            ...(this.element.dataset.options ? JSON.parse(this.element.dataset.options) : {}),
        }

        this.ref.select = getElementRef(this.element, "select", this.options)
        this.ref.placeholder = getElementRef(this.element, "placeholder", this.options)
        this.ref.dropdown = getElementRef(this.element, "dropdown", this.options)
        this.ref.list = getElementRef(this.ref.dropdown, "list", this.options)

        this.updateAllChoices()

        this.ref.select.addEventListener("change", this.handleSelectChange)
        this.ref.placeholder.addEventListener("click", this.handlePlaceholderClick)
        this.ref.list.addEventListener("click", this.handleListClick)
        this.ref.list.addEventListener("mousedown", this.handleListMouseDown)
        this.ref.list.addEventListener("mouseup", this.handleListMouseUp)
        this.element.addEventListener("blur", this.handleBlur)

        this.selectChoiceByValue(this.ref.select.value, TRIGGER_TYPES.NONE)

        this.plugins.forEach((plugin) => plugin.setup(this))
    }

    register(plugin) {
        this.plugins.push(plugin)
    }

    render(options) {
        options = {
            choices: null,
            shouldUpdateSelected: !!options.choices,
            shouldUpdatePlaceholder: false,
            ...options,
        }

        if (this.ref.select.disabled) {
            this.element.classList.add(STATES.DISABLED)
            this.element.tabIndex = -1
        } else {
            this.element.classList.remove(STATES.DISABLED)
            this.element.tabIndex = 0
        }

        this.choices = options.choices || this.allChoices

        if (this.choices) {
            this.emit(EMITS.RENDER_CHOICES_BEFORE)

            this.ref.list.innerHTML = this.choices
                .map((choice) => substitute(this.options.choiceTemplate, choice))
                .join("")

            const children = [...this.ref.list.children]
            children.forEach((element, index) => {
                const choice = this.choices[index]
                element.dataset.listitem = ""
                choice.element = element

                if (choice.value === "") {
                    element.classList.add("is-prompt")
                }

                if (choice.isDisabled()) {
                    element.classList.add("is-disabled")
                }
            })

            this.isListCreated = true
            this.emit(EMITS.RENDER_CHOICES)
        }

        if (options.shouldUpdateSelected) {
            this.choices.forEach((choice) => {
                if (!choice.element) {
                    return
                }

                if (choice.isSelected()) {
                    choice.element.classList.add("is-selected")
                } else {
                    choice.element.classList.remove("is-selected")
                }
            })
        }

        if (options.shouldUpdatePlaceholder) {
            const choice = this.allChoices.find((choice) => choice.optionEl.value === this.ref.select.value)
            if (!choice) {
                return
            }

            this.ref.placeholder.innerHTML = substitute(this.options.placeholderChoiceTemplate, choice)

            this.element.classList.remove(STATES.IS_PROMPT_SELECTED)
            this.element.classList.remove(STATES.IS_ITEM_SELECTED)
            if (choice.value === "") {
                this.element.classList.add(STATES.IS_PROMPT_SELECTED)
            } else {
                this.element.classList.add(STATES.IS_ITEM_SELECTED)
            }
        }

    }

    _setValue(value) {
        this.ref.select.value = value
        this.render({
            shouldUpdatePlaceholder: true,
            shouldUpdateSelected: true,
        })
    }

    updateAllChoices() {
        this.allChoices = [...this.ref.select.options].map((option, index) => {
            const labelParts = option.innerText.split("|")
            const choices = {
                id: index,
                value: option.value,
                optionEl: option,
                isDisabled: function () { return this.optionEl.disabled },
                isSelected: () => this.ref.select.value === option.value,
                label: labelParts.map((part) => `<span>${part}</span>`).join(""),
                element: null,
            }
            labelParts.forEach((labelPart, index) => {
                choices[`label${index}`] = labelPart
            })
            return choices
        })

        this.render({
            choices: this.allChoices
        })

        this.emit(EMITS.UPDATE_ALL_CHOICES)
    }

    getSelectedChoice() {
        return this.allChoices.find((choice) => choice.value === this.ref.select.value)
    }

    selectChoiceByValue(value, trigger = TRIGGER_TYPES.SELECT) {
        this._setValue(value)

        if (trigger === TRIGGER_TYPES.SELECT) {
            triggerEvent(this.ref.select, "focus")
            triggerEvent(this.ref.select, "change")
            this.emit(EMITS.CHANGE)
            // Nette.validateControl(this.ref.select)
            return
        }
    }

    getChoices() {
        return this.choices
    }

    handlePlaceholderClick = (event) => {
        event.preventDefault()
        this.toggleList()
    }

    handleBlur = () => {
        this.closeList()
    }

    handleSelectChange = (event) => {
        this.selectChoiceByValue(event.currentTarget.value, TRIGGER_TYPES.NONE)
    }

    handleListMouseDown = () => {
        this.isListMouseDown = true
    }

    handleListMouseUp = () => {
        this.isListMouseDown = false
        this.closeList()
    }

    handleListClick = (event) => {
        const element = event.target.closest("[data-listitem]")
        if (!element) {
            return
        }

        const value = this.choices.find((choice) => choice.element === element).value
        this.selectChoiceByValue(value)
        this.closeList()
    }

    toggleList() {
        if (this.element.classList.contains(STATES.OPEN)) {
            this.closeList()
        } else {
            this.openList()
        }
    }

    openList() {
        if (this.element.classList.contains(STATES.OPEN)) {
            return
        }

        if (!this.isListCreated) {
            this.render({
                choices: this.allChoices,
            })
        }
        this.element.classList.add(STATES.OPEN)
        this.emit(EMITS.OPEN)
    }

    closeList() {
        if (this.isListMouseDown) {
            return
        }


        this.closeRaf = requestAnimationFrame(() => {
            this.closeRaf = requestAnimationFrame(() => {
                this.element.classList.remove(STATES.OPEN)
                this.emit(EMITS.CLOSE)
            })
        })
    }
}
